import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaLaptop, FaCheck, FaTimes, FaTools, FaCheckDouble, FaInbox, FaFileExcel } from "react-icons/fa";
import * as api from "../api/itRequestApi";
import {
  T, CATEGORY_OPTIONS, PRIORITY_OPTIONS, STATUS_FILTER_OPTIONS, labelFor,
  GlobalStyles, Spinner, StatusPill,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle,
} from "./uiKit";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/* Forward-moving lifecycle: Pending → Approved → In Progress → Resolved,
   with Rejected as the one alternate terminal state off Pending. Keyed by
   current status so the Actions column always renders the right next step(s). */
const TRANSITIONS = {
  PENDING: [
    { status: "APPROVED", label: "Approve", icon: FaCheck, color: T.primary, remarksRequired: false, toastVerb: "approved" },
    { status: "REJECTED", label: "Reject", icon: FaTimes, color: T.danger, remarksRequired: true, toastVerb: "rejected" },
  ],
  APPROVED: [
    { status: "IN_PROGRESS", label: "Start Progress", icon: FaTools, color: T.cyan, remarksRequired: false, toastVerb: "marked In Progress" },
  ],
  IN_PROGRESS: [
    { status: "RESOLVED", label: "Mark Resolved", icon: FaCheckDouble, color: T.green, remarksRequired: false, toastVerb: "marked Resolved" },
  ],
};

function DecisionModal({ request, transition, onClose, onDecided }) {
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canConfirm = !transition.remarksRequired || remarks.trim().length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      await api.updateStatus(request.id, { status: transition.status, adminRemarks: remarks.trim() });
      toast.success(`Request ${transition.toastVerb}`);
      onDecided();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to update request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            {transition.label} Request
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ fontSize: 13.5, color: T.ink2, marginBottom: 14 }}>
          <strong style={{ color: T.ink1 }}>{request.title}</strong> — requested by {request.employeeName}
        </div>

        {request.description && (
          <div style={{
            background: "#f8fafc", border: `1px solid ${T.border}`, borderRadius: 9,
            padding: "10px 12px", marginBottom: 16, fontSize: 13, color: T.ink2,
            lineHeight: 1.55, whiteSpace: "pre-wrap", maxHeight: 120, overflowY: "auto",
          }} className="itreq-scroll">
            {request.description}
          </div>
        )}

        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.ink2, marginBottom: 6 }}>
          Remarks {transition.remarksRequired ? "(reason for rejection)" : "(optional)"}
        </label>
        <textarea
          autoFocus
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={transition.remarksRequired ? "Let the requester know why…" : "Any notes for the requester…"}
          rows={3}
          style={{ ...inputStyle, resize: "vertical", marginBottom: 20 }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
          <button
            type="button"
            disabled={saving || !canConfirm}
            onClick={handleConfirm}
            style={{
              ...primaryBtnStyle,
              background: transition.color,
              opacity: saving || !canConfirm ? 0.55 : 1,
              cursor: saving || !canConfirm ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : transition.label}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ITSupportAdminPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [decisionTarget, setDecisionTarget] = useState(null); // { request, transition }
  const [exporting, setExporting] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listAll(0, 200);
      setRequests(data?.content || []);
    } catch (e) {
      toast.error(e.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.exportExcel("", statusFilter, dateFrom, dateTo);
      toast.success("Downloaded successfully");
    } catch (e) {
      toast.error(e.message || "Failed to download report");
    } finally {
      setExporting(false);
    }
  };

  const clearDateFilter = () => { setDateFrom(""); setDateTo(""); };
  const hasDateFilter = Boolean(dateFrom || dateTo);

  const visibleRequests = useMemo(() => {
    return requests.filter((r) => {
      if (statusFilter !== "ALL" && r.status !== statusFilter) return false;
      const reqDate = r.createdAt ? r.createdAt.slice(0, 10) : null;
      if (dateFrom && (!reqDate || reqDate < dateFrom)) return false;
      if (dateTo && (!reqDate || reqDate > dateTo)) return false;
      return true;
    });
  }, [requests, statusFilter, dateFrom, dateTo]);

  return (
    <div style={{ flex: 1, minHeight: 0, background: T.bg, fontFamily: T.fontBody, overflowY: "auto" }} className="itreq-scroll">
      <GlobalStyles />

      <div style={{ padding: "26px 34px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: T.primary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaLaptop size={15} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>IT Support — Approvals</h2>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Review and action every IT request across the org</div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
              borderRadius: 9, border: `1px solid ${T.border}`, background: T.white, color: T.ink1,
              fontWeight: 600, fontSize: 13.5, fontFamily: T.fontBody,
              cursor: exporting ? "not-allowed" : "pointer", opacity: exporting ? 0.6 : 1,
            }}
            onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.borderColor = T.primary; }}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
          >
            <FaFileExcel size={13} color={T.green} /> {exporting ? "Exporting…" : "Export Excel"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUS_FILTER_OPTIONS.map((f) => {
              const active = statusFilter === f.value;
              return (
                <button
                  key={f.value}
                  type="button"
                  className="itreq-pill"
                  onClick={() => setStatusFilter(f.value)}
                  style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${active ? T.primary : T.border}`,
                    background: active ? T.primarySoft : T.white,
                    color: active ? T.primary : T.ink2,
                    fontFamily: T.fontBody, transition: "all .12s ease",
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>From</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              style={dateInputStyle}
            />
            <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>To</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              style={dateInputStyle}
            />
            {hasDateFilter && (
              <button type="button" onClick={clearDateFilter} style={{ ...textBtnStyle, padding: "6px 4px", fontSize: 12.5 }}>
                Clear
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : visibleRequests.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            color: T.ink3, marginTop: 56, textAlign: "center",
          }}>
            <FaInbox size={26} style={{ color: T.borderStrong }} />
            <div style={{ fontSize: 14, color: T.ink2, fontWeight: 600, fontFamily: T.fontDisplay }}>No requests here</div>
            <div style={{ fontSize: 13, maxWidth: 280 }}>
              {statusFilter === "ALL" && !hasDateFilter ? "No IT requests have been submitted yet." : "Try a different filter."}
            </div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, boxShadow: T.shadow, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <div className="itreq-scroll" style={{ overflow: "auto", maxHeight: "calc(100vh - 320px)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfc", borderBottom: `1px solid ${T.border}` }}>
                    {["S.NO", "Requester", "Category", "Title", "Description", "Priority", "Status", "Submitted", "Actions"].map((h) => (
                      <th key={h} style={theadCellStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((r, idx) => {
                    const nextSteps = TRANSITIONS[r.status];
                    return (
                      <tr key={r.id} className="itreq-row" style={{ borderBottom: idx === visibleRequests.length - 1 ? "none" : `1px solid ${T.border}` }}>
                        <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{idx + 1}</td>
                        <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1 }}>{r.employeeName}</td>
                        <td style={tdCellStyle}>{labelFor(CATEGORY_OPTIONS, r.category)}</td>
                        <td style={tdCellStyle}>{r.title}</td>
                        <td style={{ ...tdCellStyle, ...descCellStyle }} title={r.description || ""}>{r.description || "—"}</td>
                        <td style={tdCellStyle}>{labelFor(PRIORITY_OPTIONS, r.priority)}</td>
                        <td style={tdCellStyle}><StatusPill status={r.status} /></td>
                        <td style={{ ...tdCellStyle, fontFamily: T.fontMono, fontSize: 12 }}>{formatDateTime(r.createdAt)}</td>
                        <td style={tdCellStyle}>
                          {nextSteps ? (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {nextSteps.map((t) => (
                                <button
                                  key={t.status}
                                  type="button"
                                  onClick={() => setDecisionTarget({ request: r, transition: t })}
                                  style={actionBtnStyle(t.color)}
                                >
                                  <t.icon size={10} /> {t.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: T.ink3, fontSize: 12.5 }}>{r.adminRemarks || "—"}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {decisionTarget && (
        <DecisionModal
          request={decisionTarget.request}
          transition={decisionTarget.transition}
          onClose={() => setDecisionTarget(null)}
          onDecided={loadRequests}
        />
      )}
    </div>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1,
};
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13.5, color: T.ink2, verticalAlign: "top",
};
const dateInputStyle = {
  ...inputStyle, width: 148, padding: "7px 10px", fontSize: 13, cursor: "pointer",
};
const descCellStyle = {
  maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default",
};
function actionBtnStyle(color) {
  return {
    display: "flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 7,
    border: `1px solid ${color}`, background: "transparent", color, fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: T.fontBody,
  };
}
